// components/purchase/SuggestedOrders.tsx
import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast, { Toaster } from 'react-hot-toast';

interface SuggestedProduct {
  id: string;
  nom: string;
  sku: string;
  stockActuel: number;
  stockMinimum: number;
  stockMaximum: number;
  quantiteSuggeree: number;
  quantiteModifiee?: number;
  fournisseurPrefere: string;
  prixUnitaire: number;
  devise: string;
  delaiLivraison: number;
  urgence: 'Critique' | 'Urgent' | 'Normal';
  motifSuggestion: string;
}

export default function SuggestedOrders() {
  const [suggestedProducts, setSuggestedProducts] = useState<SuggestedProduct[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [groupBySupplier, setGroupBySupplier] = useState(true);
  const [showValidation, setShowValidation] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  // CHARGEMENT DES SUGGESTIONS DEPUIS L'API LARAVEL
  const generateSuggestions = async () => {
    setLoading(true);
    try {
      const res = await getResources('bons-commande/suggestions');
      setSuggestedProducts(res.data.data || []);
      setLastRefresh(new Date());
    } catch (err: any) {
      toast.error("Erreur lors du chargement des suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    generateSuggestions();
  }, []);

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === suggestedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(suggestedProducts.map(p => p.id));
    }
  };

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    setSuggestedProducts(prev =>
      prev.map(product =>
        product.id === productId
          ? { ...product, quantiteModifiee: newQuantity }
          : product
      )
    );
  };

  const getUrgencyColor = (urgence: string) => {
    switch (urgence) {
      case 'Critique': return 'text-red-600 bg-red-100';
      case 'Urgent': return 'text-orange-600 bg-orange-100';
      case 'Normal': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatAmount = (amount: number, devise: string) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' ' + devise;
  };

  const groupedProducts = groupBySupplier
    ? suggestedProducts.reduce((acc, product) => {
        const supplier = product.fournisseurPrefere;
        if (!acc[supplier]) acc[supplier] = [];
        acc[supplier].push(product);
        return acc;
      }, {} as Record<string, SuggestedProduct[]>)
    : { 'Tous les produits': suggestedProducts };

  const calculateTotalBySupplier = (products: SuggestedProduct[]) => {
    return products.reduce((total, product) => {
      if (selectedProducts.includes(product.id)) {
        const quantity = product.quantiteModifiee ?? product.quantiteSuggeree;
        return total + (quantity * product.prixUnitaire);
      }
      return total;
    }, 0);
  };

  const handleValidateSelection = () => {
    if (selectedProducts.length === 0) {
      toast.error('Veuillez sélectionner au moins un produit.');
      return;
    }
    setShowValidation(true);
  };

  const handleGenerateOrders = async () => {
    setIsGenerating(true);

    try {
      const ordersBySupplier: Record<string, any[]> = {};

      selectedProducts.forEach(productId => {
        const product = suggestedProducts.find(p => p.id === productId);
        if (!product) return;

        if (!ordersBySupplier[product.fournisseurPrefere]) {
          ordersBySupplier[product.fournisseurPrefere] = [];
        }

        ordersBySupplier[product.fournisseurPrefere].push({
          produit_id: Number(product.id),
          quantite_commandee: product.quantiteModifiee ?? product.quantiteSuggeree,
          prix_unitaire_ht: product.prixUnitaire,
        });
      });

      // Envoi groupé par fournisseur
      for (const [fournisseur, lignes] of Object.entries(ordersBySupplier)) {
        await createResource('bons-commande/suggestions', {
          suggestions: lignes, // Laravel attend "suggestions"
        });
      }

      toast.success(`${Object.keys(ordersBySupplier).length} BC créés !`);
      setSelectedProducts([]);
      setShowValidation(false);
      generateSuggestions();

    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur création BC');
    } finally {
      setIsGenerating(false);
    }
  };

  // === RENDU ===
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border border-gray-200 p-16 text-center">
        <i className="ri-loader-4-line text-5xl text-gray-400 animate-spin"></i>
        <p className="mt-4 text-gray-600">Analyse des stocks en cours...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* En-tête avec options */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Suggestions de Réapprovisionnement</h2>
            <p className="text-gray-600">
              Analyse automatique basée sur les stocks minimum et maximum
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Dernière actualisation: {lastRefresh?.toLocaleString('fr-FR') || 'Jamais'}
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={generateSuggestions}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              <span>Actualiser</span>
            </button>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={groupBySupplier}
                onChange={(e) => setGroupBySupplier(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Grouper par fournisseur</span>
            </label>
            <button
              onClick={handleSelectAll}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer"
            >
              {selectedProducts.length === suggestedProducts.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {suggestedProducts.filter(p => p.urgence === 'Critique').length}
            </div>
            <div className="text-sm text-red-700">Produits critiques</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {suggestedProducts.filter(p => p.urgence === 'Urgent').length}
            </div>
            <div className="text-sm text-orange-700">Produits urgents</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {selectedProducts.length}
            </div>
            <div className="text-sm text-blue-700">Produits sélectionnés</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {Object.keys(groupedProducts).length}
            </div>
            <div className="text-sm text-green-700">Fournisseurs concernés</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {suggestedProducts.length}
            </div>
            <div className="text-sm text-purple-700">Total suggestions</div>
          </div>
        </div>
      </div>

      {/* Liste des suggestions groupées */}
      {Object.entries(groupedProducts).map(([supplier, products]) => (
        <div key={supplier} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">{supplier}</h3>
              {groupBySupplier && (
                <div className="text-sm text-gray-600">
                  Total sélectionné: {formatAmount(calculateTotalBySupplier(products), products[0]?.devise || 'GNF')}
                </div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={products.every(p => selectedProducts.includes(p.id))}
                      onChange={() => {
                        const allSelected = products.every(p => selectedProducts.includes(p.id));
                        if (allSelected) {
                          setSelectedProducts(prev => prev.filter(id => !products.map(p => p.id).includes(id)));
                        } else {
                          setSelectedProducts(prev => [...prev, ...products.map(p => p.id).filter(id => !prev.includes(id))]);
                        }
                      }}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stock Actuel
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantité Suggérée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prix Unitaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Urgence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Motif
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{product.nom}</div>
                      <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{product.stockActuel}</span> unités
                        </div>
                        <div className="text-sm text-gray-500">
                          Min: {product.stockMinimum} | Max: {product.stockMaximum}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          max={product.stockMaximum}
                          value={product.quantiteModifiee ?? product.quantiteSuggeree}
                          onChange={(e) => handleQuantityChange(product.id, parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-sm text-gray-500">unités</span>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Total: {formatAmount((product.quantiteModifiee ?? product.quantiteSuggeree) * product.prixUnitaire, product.devise)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {formatAmount(product.prixUnitaire, product.devise)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getUrgencyColor(product.urgence)}`}>
                        {product.urgence}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs">
                        {product.motifSuggestion}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        Délai: {product.delaiLivraison} jours
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Actions en bas - EXACTEMENT COMME TU VEUX */}
      {selectedProducts.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                {selectedProducts.length} produit(s) sélectionné(s)
              </h3>
              <p className="text-gray-600">
                Validation et création automatique des bons de commande
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setSelectedProducts([])}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                Annuler la sélection
              </button>
              <button
                onClick={handleValidateSelection}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center space-x-2"
              >
                <i className="ri-check-line"></i>
                <span>Valider la Sélection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de validation - IDENTIQUE À TON CODE */}
      {showValidation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-900">
                  Validation des Bons de Commande
                </h3>
                <button
                  onClick={() => setShowValidation(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  Résumé de la commande
                </h4>
                <p className="text-gray-600">
                  Les bons de commande suivants seront créés automatiquement:
                </p>
              </div>

              {/* Résumé par fournisseur */}
              {Object.entries(groupedProducts).map(([supplier, products]) => {
                const selectedSupplierProducts = products.filter(p => selectedProducts.includes(p.id));
                if (selectedSupplierProducts.length === 0) return null;

                return (
                  <div key={supplier} className="mb-6 border border-gray-200 rounded-lg">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h5 className="font-medium text-gray-900">{supplier}</h5>
                      <p className="text-sm text-gray-600">
                        {selectedSupplierProducts.length} produit(s) - 
                        Total: {formatAmount(calculateTotalBySupplier(products), selectedSupplierProducts[0]?.devise || 'GNF')}
                      </p>
                    </div>
                    <div className="p-4">
                      <div className="space-y-2">
                        {selectedSupplierProducts.map(product => (
                          <div key={product.id} className="flex justify-between items-center text-sm">
                            <span className="text-gray-900">{product.nom}</span>
                            <span className="text-gray-600">
                              {product.quantiteModifiee ?? product.quantiteSuggeree} × {formatAmount(product.prixUnitaire, product.devise)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                  <div>
                    <h5 className="font-medium text-blue-900 mb-1">Information importante</h5>
                    <p className="text-sm text-blue-800">
                      Les bons de commande seront créés en statut "Brouillon" et pourront être modifiés avant envoi aux fournisseurs.
                      Les quantités peuvent encore être ajustées dans le formulaire de commande.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-4">
              <button
                onClick={() => setShowValidation(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors cursor-pointer"
                disabled={isGenerating}
              >
                Annuler
              </button>
              <button
                onClick={handleGenerateOrders}
                disabled={isGenerating}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors cursor-pointer flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Génération en cours...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-file-add-line"></i>
                    <span>Générer les Bons de Commande</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aucun réapprovisionnement nécessaire */}
      {suggestedProducts.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <i className="ri-check-double-line text-4xl text-green-500 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun réapprovisionnement nécessaire</h3>
          <p className="text-gray-500 mb-4">
            Tous vos produits ont des niveaux de stock suffisants selon les seuils définis.
          </p>
          <button
            onClick={generateSuggestions}
            className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
          >
            <i className="ri-refresh-line mr-1"></i>
            Actualiser l'analyse
          </button>
        </div>
      )}
    </div>
  );
}